import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card, Row, Col, Button, Form, Spinner, Alert, Badge } from 'react-bootstrap';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { LineChart, BarChart, PieChart } from 'echarts/charts';
import {
  GridComponent, TooltipComponent, LegendComponent, DataZoomComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import infomediaService from '../../services/infomediaService';
import budgetService from '../../services/budgetService';
import { useAuth } from '../../contexts/AuthContext';

echarts.use([LineChart, BarChart, PieChart, GridComponent, TooltipComponent, LegendComponent, DataZoomComponent, CanvasRenderer]);

/* ═══════════════ constantes / helpers ═══════════════ */
const MES = ['', 'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const CY = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => CY - i);
const CLIENT_OPTIONS = [
  { companyId: 16, label: 'Claro' },
  { companyId: 28, label: 'Tigo' },
  { companyId: 17, label: 'Movistar' },
  { companyId: 1, label: 'Ford' },
];
const GRUPO_MEDIOS = ['ALL', 'TV', 'RADIO', 'OOH', 'IMPRESOS', 'DIGITAL'];
const BRAND_COLORS = ['#e11d48', '#0ea5e9', '#f59e0b', '#10b981', '#8b5cf6', '#6366f1', '#d946ef', '#14b8a6', '#f97316', '#06b6d4', '#84cc16', '#a855f7', '#64748b', '#ec4899', '#22d3ee'];
const PLAT_COLOR = { Meta: '#1877f2', Facebook: '#1877f2', Google: '#ea4335', 'Google Ads': '#ea4335', TikTok: '#000000', LinkedIn: '#0a66c2', Illumin: '#8b5cf6', Programatic: '#8b5cf6', YouTube: '#ff0000', X: '#111111' };
const RD = '#c4636e';
const n = (x) => Number(x) || 0;
const fmt$ = (v) => { if (v == null || isNaN(v)) return '—'; const a = Math.abs(v); if (a >= 1e6) return `$${(v / 1e6).toFixed(2)}M`; if (a >= 1e3) return `$${(v / 1e3).toFixed(1)}K`; return `$${v.toFixed(0)}`; };
const fmtN = (v) => { if (v == null || isNaN(v)) return '—'; const a = Math.abs(v); if (a >= 1e6) return `${(v / 1e6).toFixed(2)}M`; if (a >= 1e3) return `${(v / 1e3).toFixed(1)}K`; return `${Number(v).toLocaleString()}`; };

// Digital = SIEMPRE un mes (el backend de campaign-metrics es lento y desborda INT en
// rangos grandes de Claro). Si no hay mes elegido: mes actual (año en curso) o dic (años pasados).
function digitalMonth(year, month) {
  if (month) return Number(month);
  return year === CY ? (new Date().getMonth() + 1) : 12;
}
function monthRange(year, month) {
  const m = digitalMonth(year, month);
  const mm = String(m).padStart(2, '0');
  const last = new Date(year, m, 0).getDate();
  return { from: `${year}-${mm}-01`, to: `${year}-${mm}-${String(last).padStart(2, '0')}`, month: m };
}

/* ═══════════════ MultiSelectFilter (contenido, sin desbordes) ═══════════════ */
function MultiSelectFilter({ label, options, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  const filtered = (options || []).filter(o => o.toLowerCase().includes(search.toLowerCase()));
  const allSelected = selected.length === 0;
  const toggle = (val) => { if (selected.includes(val)) onChange(selected.filter(v => v !== val)); else onChange([...selected, val]); };
  const displayText = allSelected ? 'Todas' : selected.length <= 2 ? selected.join(', ') : `${selected.length} seleccionadas`;
  return (
    <div ref={ref} className="mb-2 position-relative">
      <Form.Label style={{ fontSize: 10 }} className="text-muted text-uppercase mb-0">{label}</Form.Label>
      <div className="form-select form-select-sm d-flex align-items-center"
        style={{ cursor: 'pointer', fontSize: 11, minHeight: 31, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        onClick={() => setOpen(!open)} title={allSelected ? 'Todas' : selected.join(', ')}>
        {displayText}
      </div>
      {open && (
        <div className="position-absolute bg-white border rounded shadow-sm" style={{ zIndex: 9999, width: '100%', maxHeight: 260, overflowY: 'auto', top: '100%', left: 0 }}>
          <div className="p-1 border-bottom">
            <input type="text" className="form-control form-control-sm" placeholder="Buscar..." value={search} onChange={e => setSearch(e.target.value)} autoFocus style={{ fontSize: 11 }} />
          </div>
          <div className="px-2 py-1 border-bottom" style={{ fontSize: 11 }}>
            <Form.Check type="checkbox" label="Seleccionar todo" checked={allSelected} onChange={() => onChange([])} id={`ms-all-${label}`} style={{ fontSize: 11 }} />
          </div>
          <div style={{ maxHeight: 190, overflowY: 'auto' }}>
            {filtered.map(opt => (
              <div key={opt} className="px-2 py-0" style={{ fontSize: 11 }}>
                <Form.Check type="checkbox" label={opt} checked={selected.includes(opt)} onChange={() => toggle(opt)} id={`ms-${label}-${opt}`} style={{ fontSize: 11 }} />
              </div>
            ))}
            {filtered.length === 0 && <div className="px-2 py-1 text-muted" style={{ fontSize: 10 }}>Sin resultados</div>}
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════ ECharts wrapper + option builders ═══════════════ */
const EChart = ({ option, height = 280, onEvents }) => (
  <ReactEChartsCore echarts={echarts} option={option} style={{ height }} notMerge lazyUpdate opts={{ renderer: 'canvas' }} onEvents={onEvents} />
);

function donutOption(data, colorFn) {
  return {
    tooltip: { trigger: 'item', formatter: p => `${p.name}: ${fmt$(p.value)} (${p.percent}%)` },
    legend: { type: 'scroll', orient: 'vertical', right: 4, top: 'center', textStyle: { fontSize: 10 } },
    series: [{
      type: 'pie', radius: ['45%', '72%'], center: ['36%', '52%'], avoidLabelOverlap: true,
      itemStyle: { borderColor: '#fff', borderWidth: 1 },
      label: { formatter: '{d}%', fontSize: 10 },
      data: data.map((d, i) => ({ name: d.name, value: d.value, itemStyle: { color: (colorFn && colorFn(d.name, i)) || BRAND_COLORS[i % BRAND_COLORS.length] } })),
    }],
  };
}

function barVarOption(cats, bars, varPct, rotate = 0) {
  return {
    tooltip: { trigger: 'axis' },
    legend: { bottom: 0, textStyle: { fontSize: 9 } },
    grid: { left: 58, right: 46, top: 16, bottom: 46 },
    xAxis: { type: 'category', data: cats, axisLabel: { fontSize: 9, rotate, interval: 0 } },
    yAxis: [
      { type: 'value', axisLabel: { formatter: v => fmt$(v), fontSize: 9 } },
      { type: 'value', axisLabel: { formatter: v => `${v}%`, fontSize: 9 }, splitLine: { show: false } },
    ],
    series: [
      { name: 'Inversión', type: 'bar', data: bars, itemStyle: { color: '#e8b4b8', borderRadius: [3, 3, 0, 0] } },
      { name: 'Var%', type: 'line', yAxisIndex: 1, data: varPct, smooth: true, itemStyle: { color: RD }, lineStyle: { color: RD } },
    ],
  };
}

function multiLineOption(cats, series) {
  return {
    tooltip: { trigger: 'axis' },
    legend: { type: 'scroll', bottom: 0, textStyle: { fontSize: 9 } },
    grid: { left: 58, right: 16, top: 16, bottom: 46 },
    xAxis: { type: 'category', data: cats, axisLabel: { fontSize: 8, rotate: 40 } },
    yAxis: { type: 'value', axisLabel: { formatter: v => fmt$(v), fontSize: 9 } },
    series: series.map((s, i) => ({ name: s.name, type: 'line', data: s.data, smooth: true, showSymbol: false, lineStyle: { width: 2, color: BRAND_COLORS[i % BRAND_COLORS.length] }, itemStyle: { color: BRAND_COLORS[i % BRAND_COLORS.length] } })),
  };
}

function barOption(cats, values, colorFn, rotate = 0) {
  return {
    tooltip: { trigger: 'axis', formatter: p => `${p[0].name}: ${fmt$(p[0].value)}` },
    grid: { left: 62, right: 16, top: 12, bottom: rotate ? 60 : 30 },
    xAxis: { type: 'category', data: cats, axisLabel: { fontSize: 9, rotate, interval: 0 } },
    yAxis: { type: 'value', axisLabel: { formatter: v => fmt$(v), fontSize: 9 } },
    series: [{ type: 'bar', data: values.map((v, i) => ({ value: v, itemStyle: { color: (colorFn && colorFn(cats[i], i)) || '#e8b4b8', borderRadius: [3, 3, 0, 0] } })) }],
  };
}

function ChartCard({ title, right, children, h }) {
  return (
    <Card className="border-0 shadow-sm h-100"><Card.Body className="p-2">
      <div className="d-flex justify-content-between align-items-center mb-1">
        <h6 className="text-uppercase fw-bold mb-0" style={{ fontSize: 11 }}>{title}</h6>
        {right}
      </div>
      <div style={{ height: h || 260 }}>{children}</div>
    </Card.Body></Card>
  );
}
const NoData = () => <div className="d-flex align-items-center justify-content-center text-muted small h-100">Sin datos</div>;

/* ═══════════════ Componente principal ═══════════════ */
export default function CompetenciaDashboard({ atlOnly = false }) {
  const { user, assignedClientIds } = useAuth();
  const myClients = useMemo(() => assignedClientIds || [], [assignedClientIds]);
  const myClient = myClients.length ? Number(myClients[0]) : null;
  // El selector de cliente SOLO para quien puede ver varios (super-admin o multi/no-cliente).
  // Un usuario de un solo cliente (p.ej. Claro) queda fijo a su cliente, sin selector.
  const showClientSelector = !!(user?.isSuperAdmin || myClients.length !== 1);

  const defaults = useMemo(() => ({
    companyId: myClient, year: CY, month: '', grupoMedio: 'ALL',
    categorias: [], marcas: [], segmentos: [], platforms: [], productTypes: [],
    // Dimensiones de drill (clic en gráficas): sector, emisora/red, género, medio, bloque.
    sectores: [], redes: [], generos: [], medios: [], bloques: [],
    // Admin / super-admin entran viendo TODO el mercado (no por cliente); un
    // usuario de un solo cliente arranca con su scope de marca.
    mercadoTotal: showClientSelector,
  }), [myClient, showClientSelector]);

  const [tab, setTab] = useState('atl'); // 'atl' | 'digital' | 'data'
  // filtros PENDIENTES (ligados a inputs) y APLICADOS (snapshot que dispara la búsqueda)
  const [pend, setPend] = useState(defaults);
  const [applied, setApplied] = useState(defaults);
  const setP = (patch) => setPend(p => ({ ...p, ...patch }));

  const [atlData, setAtlData] = useState(null);
  const [digData, setDigData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [narrativa, setNarrativa] = useState(null);
  const [narrLoading, setNarrLoading] = useState(false);

  const clientIdFor = (f) => f.companyId || myClient || 16;

  const loadATL = useCallback(async (f) => {
    setLoading(true); setError(null);
    try {
      const res = await infomediaService.getCompetenciaDashboard({
        year: f.year, month: f.month || undefined, grupoMedio: f.grupoMedio,
        categoria: f.categorias.length ? f.categorias.join(',') : undefined,
        marca: f.marcas.length ? f.marcas.join(',') : undefined,
        segmento: f.segmentos.length ? f.segmentos.join(',') : undefined,
        sector: f.sectores?.length ? f.sectores.join(',') : undefined,
        red: f.redes?.length ? f.redes.join(',') : undefined,
        genero: f.generos?.length ? f.generos.join(',') : undefined,
        medio: f.medios?.length ? f.medios.join(',') : undefined,
        bloque: f.bloques?.length ? f.bloques.join(',') : undefined,
        companyId: f.companyId || undefined,
        mercadoTotal: f.mercadoTotal || undefined,
      });
      setAtlData(res); setNarrativa(null);
    } catch (e) { setError(e.message || 'Error'); }
    finally { setLoading(false); }
  }, []);

  const loadDigital = useCallback(async (f) => {
    setLoading(true); setError(null);
    try {
      const { from, to } = monthRange(f.year, f.month);
      const res = await budgetService.getCampaignDashboardMetrics({
        clientId: clientIdFor(f), dateFrom: from, dateTo: to,
        platform: f.platforms.length ? f.platforms.join(',') : undefined,
        productType: f.productTypes.length ? f.productTypes.join(',') : undefined,
        light: true, // omite adGroups/ads/región/edad que esta pestaña no muestra → más rápido
      });
      setDigData(res?.success ? res.data : (res?.data || res));
    } catch (e) { setError(e.message || 'Error'); }
    finally { setLoading(false); }
  }, []);

  // Recarga el tab activo cuando cambian los filtros APLICADOS (incluye drill). Usa una
  // "firma" para NO recargar en meros cambios de tab, y mantiene la data anterior visible
  // (con overlay) mientras llega la nueva → el drill no parpadea en blanco.
  const lastAtlKey = useRef(null);
  const lastDigKey = useRef(null);
  useEffect(() => {
    const key = JSON.stringify(applied);
    if (tab === 'atl' || tab === 'data') {
      if (lastAtlKey.current !== key || !atlData) { lastAtlKey.current = key; loadATL(applied); }
    } else if (tab === 'digital') {
      if (lastDigKey.current !== key || !digData) { lastDigKey.current = key; loadDigital(applied); }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, applied]);

  // Buscar/Limpiar actualizan el snapshot APLICADO; el useEffect recarga el tab activo.
  const buscar = () => { setApplied({ ...pend }); };
  const limpiar = () => { setPend(defaults); setApplied(defaults); };
  // Drill down/up: aplica un patch de filtros al instante (sin pulsar Buscar). NO borra la
  // data actual → el useEffect detecta el cambio de firma y recarga con overlay.
  const onDrill = (patch) => {
    setPend(p => ({ ...p, ...patch }));
    setApplied(a => ({ ...a, ...patch }));
  };

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      let rows = [];
      let fname = '';
      if (tab === 'digital') {
        rows = (digData?.campaigns || []).map(c => ({
          Cuenta: c.accountName, Campaña: c.campaignName, Plataforma: c.sourcePlatform, Producto: c.productType,
          Inversión: n(c.cost), Impresiones: n(c.impressions), Clicks: n(c.clicks), Leads: n(c.leads),
          CPM: n(c.cpm), CPC: n(c.cpc), CPL: n(c.cpl), CTR: n(c.ctr),
        }));
        fname = `digital_${clientIdFor(applied)}_${applied.year}${applied.month ? '-' + applied.month : ''}.xlsx`;
      } else {
        const res = await infomediaService.getCompetenciaExport({
          year: applied.year, month: applied.month || undefined, grupoMedio: applied.grupoMedio,
          categoria: applied.categorias.length ? applied.categorias.join(',') : undefined,
          marca: applied.marcas.length ? applied.marcas.join(',') : undefined,
          segmento: applied.segmentos.length ? applied.segmentos.join(',') : undefined,
          companyId: applied.companyId || undefined, limit: 20000,
          mercadoTotal: applied.mercadoTotal || undefined,
        });
        rows = (res?.rows || []).map(r => ({ Año: r.ano, Mes: r.mes, Semana: r.semana, Categoría: r.categoria, Marca: r.marca, Medio: r.medio, Emisora: r.emisora, Inversión: r.inversion, Avisos: r.avisos }));
        fname = `competencia_atl_${applied.year}${applied.month ? '-' + applied.month : ''}.xlsx`;
      }
      if (!rows.length) { setError('No hay datos para exportar con estos filtros'); return; }
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Datos');
      const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      saveAs(new Blob([buf], { type: 'application/octet-stream' }), fname);
    } catch (e) { setError(e.message || 'Error exportando'); }
    finally { setExporting(false); }
  }, [tab, digData, applied]);

  const loadNarrativa = useCallback(async (forzar = false) => {
    if (!atlData?.porMarca?.length) return;
    setNarrLoading(true);
    try {
      const periodo = `${applied.month ? MES[applied.month] + ' ' : ''}${applied.year}`;
      const prefijo = applied.mercadoTotal
        ? 'Mercado '
        : (applied.segmentos.length ? applied.segmentos.join('/') + ' ' : '');
      const res = await infomediaService.getCompetenciaNarrativa({
        periodoEtiqueta: prefijo + periodo,
        marcaFoco: applied.mercadoTotal ? null : ((atlData.focusBrands || [])[0] || null),
        kpis: atlData.kpis || null, porMarca: atlData.porMarca, serieAnual: atlData.serieAnual || [],
        forzar: forzar === true,
      });
      setNarrativa(res);
    } catch (e) { setError(e.message || 'Error IA'); }
    finally { setNarrLoading(false); }
  }, [atlData, applied]);

  // Auto-generar la narrativa al cargar el ATL (usa la data del año/vista actual).
  // El backend cachea en BD, así que reabrir la misma vista no vuelve a llamar a la IA.
  useEffect(() => {
    if (tab === 'atl' && atlData?.porMarca?.length && !narrativa && !narrLoading) {
      loadNarrativa(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, atlData]);

  const opts = atlData?.filterOptions;
  const digPlatforms = useMemo(() => (digData?.byPlatform || []).map(p => p.platform).filter(Boolean), [digData]);
  const digProducts = useMemo(() => (digData?.byProductType || []).map(p => p.productType).filter(Boolean), [digData]);

  const TABS = atlOnly
    ? [{ id: 'atl', label: '📺 ATL Competencia' }]
    : [
        { id: 'atl', label: '📺 ATL Competencia' },
        { id: 'digital', label: '📱 Digital (propio)' },
        { id: 'data', label: '📄 Data' },
      ];

  return (
    <div className="pb-4">
      <Row>
        {/* ═══ SIDEBAR filtros ═══ */}
        <Col lg={2} className="mb-3">
          <Card className="border-0 shadow-sm"><Card.Body className="p-2">
            <div className="text-muted text-uppercase fw-bold mb-2" style={{ fontSize: 10, letterSpacing: 1 }}>Filtros</div>
            {showClientSelector && (
              <>
                <Form.Label style={{ fontSize: 10 }} className="text-muted text-uppercase mb-0">Cliente</Form.Label>
                <Form.Select size="sm" className="mb-2" style={{ fontSize: 11 }} value={pend.companyId ?? ''} onChange={e => setP({ companyId: e.target.value ? Number(e.target.value) : null })}>
                  <option value="">— Mi cliente —</option>
                  {CLIENT_OPTIONS.map(c => <option key={c.companyId} value={c.companyId}>{c.label}</option>)}
                </Form.Select>
              </>
            )}
            <Row className="g-1 mb-1">
              <Col xs={6}>
                <Form.Label style={{ fontSize: 10 }} className="text-muted text-uppercase mb-0">Año</Form.Label>
                <Form.Select size="sm" style={{ fontSize: 11 }} value={pend.year} onChange={e => setP({ year: Number(e.target.value) })}>
                  {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                </Form.Select>
              </Col>
              <Col xs={6}>
                <Form.Label style={{ fontSize: 10 }} className="text-muted text-uppercase mb-0">Mes</Form.Label>
                <Form.Select size="sm" style={{ fontSize: 11 }} value={pend.month} onChange={e => setP({ month: e.target.value })}>
                  <option value="">Todos</option>
                  {MES.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
                </Form.Select>
              </Col>
            </Row>
            {(tab === 'atl' || tab === 'data') && (<>
              <div className="mb-2 p-1 rounded" style={{ background: pend.mercadoTotal ? '#fef3c7' : 'transparent' }}>
                <Form.Check type="switch" id="mercado-total"
                  label={<span style={{ fontSize: 11, fontWeight: 600 }}>🌐 Mercado Total</span>}
                  checked={!!pend.mercadoTotal}
                  onChange={e => setP({ mercadoTotal: e.target.checked })}
                  title="Todo el mercado (todas las categorías/marcas), no solo tu cliente" />
              </div>
              <Form.Label style={{ fontSize: 10 }} className="text-muted text-uppercase mb-0">Medio</Form.Label>
              <Form.Select size="sm" className="mb-2" style={{ fontSize: 11 }} value={pend.grupoMedio} onChange={e => setP({ grupoMedio: e.target.value })}>
                {GRUPO_MEDIOS.map(g => <option key={g} value={g}>{g === 'ALL' ? 'Todos' : g}</option>)}
              </Form.Select>
              <MultiSelectFilter label="Categoría" options={opts?.categorias || []} selected={pend.categorias} onChange={v => setP({ categorias: v })} />
              <MultiSelectFilter label="Marca" options={opts?.marcas || []} selected={pend.marcas} onChange={v => setP({ marcas: v })} />
              <MultiSelectFilter label="Segmento" options={opts?.segmentos || []} selected={pend.segmentos} onChange={v => setP({ segmentos: v })} />
            </>)}
            {tab === 'digital' && (<>
              <MultiSelectFilter label="Plataforma" options={digPlatforms} selected={pend.platforms} onChange={v => setP({ platforms: v })} />
              <MultiSelectFilter label="Producto" options={digProducts} selected={pend.productTypes} onChange={v => setP({ productTypes: v })} />
            </>)}
            <div className="d-grid gap-1 mt-2">
              <Button size="sm" variant="primary" onClick={buscar} disabled={loading} style={{ fontSize: 12, fontWeight: 600 }}>
                {loading ? <><Spinner animation="border" size="sm" className="me-1" />Buscando…</> : '🔍 Buscar'}
              </Button>
              <Button size="sm" variant="outline-secondary" onClick={limpiar} disabled={loading} style={{ fontSize: 11 }}>Limpiar</Button>
              <Button size="sm" variant="dark" onClick={handleExport} disabled={exporting || loading} style={{ fontSize: 11 }}>
                {exporting ? <><Spinner animation="border" size="sm" className="me-1" />Exportando…</> : 'Descargar datos'}
              </Button>
            </div>
          </Card.Body></Card>
        </Col>

        {/* ═══ MAIN ═══ */}
        <Col lg={10}>
          {/* Top nav */}
          <div className="d-flex gap-1 mb-3 flex-wrap align-items-center">
            {atlOnly ? (
              <h5 className="mb-0 fw-bold">📺 ATL Competencia</h5>
            ) : (
              TABS.map(t => (
                <Button key={t.id} size="sm" variant={tab === t.id ? 'primary' : 'outline-secondary'}
                  onClick={() => setTab(t.id)} style={{ fontSize: 12, fontWeight: tab === t.id ? 700 : 400 }}>
                  {t.label}
                </Button>
              ))
            )}
            <div className="ms-auto text-muted small align-self-center">
              {applied.companyId ? (CLIENT_OPTIONS.find(c => c.companyId === applied.companyId)?.label || applied.companyId) : ''} · {applied.month ? MES[applied.month] + ' ' : ''}{applied.year}
            </div>
          </div>

          {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
          {loading && !atlData && !digData && <div className="text-center py-5"><Spinner animation="border" /></div>}

          {tab === 'atl' && <ATLView data={atlData} narrativa={narrativa} narrLoading={narrLoading} onNarrativa={loadNarrativa} applied={applied} onDrill={onDrill} loading={loading} />}
          {tab === 'digital' && <DigitalView data={digData} />}
          {tab === 'data' && <DataView data={atlData} />}
        </Col>
      </Row>
    </div>
  );
}

/* ═══════════════ ATL VIEW (competencia Infomedia) ═══════════════ */
function ATLView({ data, narrativa, narrLoading, onNarrativa, applied = {}, onDrill, loading }) {
  if (!data) return null;
  const anual = [...(data.serieAnual || [])].sort((a, b) => a.ano - b.ano);
  const mensual = [...(data.serieMensual || [])].sort((a, b) => a.ano === b.ano ? a.mes - b.mes : a.ano - b.ano);
  const semanal = data.serieSemanalMarca || [];
  const anualMarca = data.serieAnualMarca || [];
  const porMarca = data.porMarca || [];
  const porMedio = data.porMedio || [];
  const kpi = data.kpis || {};

  const anualChart = anual.length ? {
    tooltip: { trigger: 'axis', formatter: p => `${p[0].name}: ${fmt$(p[0].value)}` },
    grid: { left: 58, right: 16, top: 16, bottom: 24 },
    xAxis: { type: 'category', data: anual.map(r => r.ano) },
    yAxis: { type: 'value', axisLabel: { formatter: v => fmt$(v), fontSize: 9 } },
    series: [{ type: 'line', data: anual.map(r => n(r.inversion)), smooth: true, areaStyle: { color: 'rgba(196,99,110,0.15)' }, lineStyle: { color: RD, width: 3 }, itemStyle: { color: RD }, symbolSize: 7 }],
  } : null;

  const mensualChart = mensual.length ? barVarOption(
    mensual.map(r => `${MES[r.mes] || r.mes} ${String(r.ano).slice(2)}`),
    mensual.map(r => n(r.inversion)),
    mensual.map((r, i) => i === 0 ? null : (() => { const p = n(mensual[i - 1].inversion); const c = n(r.inversion); return p > 0 ? +(((c - p) / p) * 100).toFixed(1) : null; })()),
    mensual.length > 10 ? 30 : 0,
  ) : null;

  const semBrands = [...new Set(semanal.map(r => r.marca))];
  const semWeeks = [...new Set(semanal.map(r => r.semana))].sort((a, b) => a - b);
  const semanalChart = semanal.length ? multiLineOption(
    semWeeks.map(w => `W${w}`),
    semBrands.map(b => ({ name: b, data: semWeeks.map(w => { const m = semanal.find(r => r.semana === w && r.marca === b); return m ? n(m.inversion) : 0; }) })),
  ) : null;

  const donaMarca = porMarca.length ? donutOption(porMarca.slice(0, 8).map(r => ({ name: r.marca, value: n(r.inversion) }))) : null;
  const donaMedio = porMedio.length ? donutOption(porMedio.map(r => ({ name: r.nombre, value: n(r.inversion) }))) : null;

  const histYears = [...new Set(anualMarca.map(r => r.ano))].sort((a, b) => a - b);
  const histBrands = [...new Set(anualMarca.map(r => r.marca))];
  const totByYear = {}; histYears.forEach(y => { totByYear[y] = anualMarca.filter(r => r.ano === y).reduce((s, r) => s + n(r.inversion), 0); });
  const histRows = histBrands.map(b => ({ marca: b, cells: histYears.map(y => { const rec = anualMarca.find(r => r.ano === y && r.marca === b); return rec ? n(rec.inversion) : 0; }) }))
    .sort((a, b) => b.cells.reduce((s, v) => s + v, 0) - a.cells.reduce((s, v) => s + v, 0));
  const tipoColor = { 'Líder': 'success', 'Estacional': 'info', 'Seguidor': 'warning', 'Táctico': 'secondary' };

  // ── Breakdowns enriquecidos (el backend ya los devuelve) ──
  const aggBy = (rows, key, valKey = 'inversion') => {
    const m = {}; (rows || []).forEach(r => { const k = r[key]; if (k) m[k] = (m[k] || 0) + n(r[valKey]); });
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
  };
  const hbar = (entries, max = 12) => {
    const top = entries.slice(0, max).reverse();
    if (!top.length) return null;
    return {
      tooltip: { trigger: 'axis', formatter: p => `${p[0].name}: ${fmt$(p[0].value)}` },
      grid: { left: 135, right: 20, top: 8, bottom: 22 },
      xAxis: { type: 'value', axisLabel: { formatter: v => fmt$(v), fontSize: 8 } },
      yAxis: { type: 'category', data: top.map(([k]) => k), axisLabel: { fontSize: 9, width: 125, overflow: 'truncate' } },
      series: [{ type: 'bar', data: top.map(([, v]) => v), itemStyle: { color: '#e8b4b8', borderRadius: [0, 3, 3, 0] } }],
    };
  };
  const catChart = hbar((data.porCategoria || []).map(r => [r.nombre, n(r.inversion)]));
  const sectorChart = hbar((data.porSector || []).map(r => [r.nombre, n(r.inversion)]));
  const emisoraChart = hbar(aggBy(data.porRedMarca, 'red'));
  const franjaChart = hbar(aggBy(data.porBloqueMarca, 'bloque'), 8);
  const generoEntries = aggBy(data.porGeneroMarca, 'genero');
  const generoChart = generoEntries.length ? donutOption(generoEntries.slice(0, 8).map(([k, v]) => ({ name: k, value: v }))) : null;
  // Share por medio por año (barras apiladas)
  const MEDIO_COLOR = { TV: '#e11d48', RADIO: '#f59e0b', OOH: '#10b981', IMPRESOS: '#8b5cf6', DIGITAL: '#0ea5e9', OTROS: '#64748b' };
  const sam = data.serieAnualMedio || [];
  const samYears = [...new Set(sam.map(r => r.ano))].sort((a, b) => a - b);
  const samMedios = [...new Set(sam.map(r => r.medio))];
  const shareMedioChart = sam.length ? {
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' }, formatter: ps => `${ps[0].axisValue}<br/>` + ps.map(p => `${p.marker}${p.seriesName}: ${fmt$(p.value)}`).join('<br/>') },
    legend: { bottom: 0, textStyle: { fontSize: 9 } },
    grid: { left: 58, right: 16, top: 12, bottom: 40 },
    xAxis: { type: 'category', data: samYears },
    yAxis: { type: 'value', axisLabel: { formatter: v => fmt$(v), fontSize: 9 } },
    series: samMedios.map(md => ({ name: md, type: 'bar', stack: 'x', data: samYears.map(y => { const r = sam.find(x => x.ano === y && x.medio === md); return r ? n(r.inversion) : 0; }), itemStyle: { color: MEDIO_COLOR[md] || '#94a3b8' } })),
  } : null;

  // ── Drill down (clic en cualquier gráfica) / drill up (breadcrumb) ──
  const drill = onDrill || (() => {});
  // Añade el valor clicado como filtro de esa dimensión (toggle: si ya está, lo quita).
  const clickAdd = (dimKey) => ({ click: (p) => { const name = p?.name; if (!name) return; const cur = applied[dimKey] || []; drill({ [dimKey]: cur.includes(name) ? cur.filter(x => x !== name) : [...cur, name] }); } });
  const clickYear = { click: (p) => { const y = Number(p?.name); if (y) drill({ year: y }); } };
  const clickMonth = { click: (p) => { const r = mensual[p?.dataIndex]; if (r) drill({ year: r.ano, month: String(r.mes) }); } };
  const removeFrom = (dimKey, val) => drill({ [dimKey]: (applied[dimKey] || []).filter(x => x !== val) });
  // Breadcrumb unificado: TODAS las dimensiones de drill activas, cada una removible (subir nivel).
  const dimCrumbs = [
    ['categorias', 'Categoría'], ['marcas', 'Marca'], ['sectores', 'Sector'],
    ['medios', 'Medio'], ['redes', 'Emisora'], ['generos', 'Género'],
    ['bloques', 'Franja'], ['segmentos', 'Segmento'],
  ];
  const crumbs = [
    ...dimCrumbs.flatMap(([k, label]) => (applied[k] || []).map(v => ({ label: `${label}: ${v}`, clear: () => removeFrom(k, v) }))),
    ...(applied.grupoMedio && applied.grupoMedio !== 'ALL' ? [{ label: `Grupo: ${applied.grupoMedio}`, clear: () => drill({ grupoMedio: 'ALL' }) }] : []),
    ...(applied.month ? [{ label: `Mes: ${MES[applied.month] || applied.month}`, clear: () => drill({ month: '' }) }] : []),
  ];

  return (<div style={{ position: 'relative' }}>
    {/* Overlay de carga en drill: mantiene la vista y muestra que está recargando */}
    {loading && (
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.55)', zIndex: 20, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 80 }}>
        <div className="text-center"><Spinner animation="border" /><div className="small text-muted mt-2">Cargando…</div></div>
      </div>
    )}
    <div className="d-flex flex-wrap align-items-center gap-2 mb-2 p-2 rounded" style={{ background: '#f1f5f9' }}>
      <span className="text-muted small fw-semibold">🔎 Explorando:</span>
      <span className="badge bg-primary" style={{ fontSize: 11 }}>Año: {applied.year}{applied.mercadoTotal ? ' · Mercado total' : ''}</span>
      {crumbs.map((c, i) => (
        <span key={i} className="badge bg-white text-dark border" style={{ fontSize: 11, cursor: 'pointer' }} onClick={c.clear} title="Quitar este filtro (subir un nivel)">
          {c.label} <span style={{ color: '#dc2626' }}>✕</span>
        </span>
      ))}
      {crumbs.length > 0 && (
        <Button size="sm" variant="outline-secondary" style={{ fontSize: 11, padding: '1px 8px' }} onClick={() => drill(Object.fromEntries(dimCrumbs.map(([k]) => [k, []]).concat([['grupoMedio', 'ALL'], ['month', '']])))} title="Quitar todos los filtros de drill">⬆ Volver a ver todo</Button>
      )}
      {crumbs.length === 0 && <span className="text-muted small">clic en cualquier gráfica para profundizar</span>}
    </div>
    <Row className="g-2 mb-2">
      {[
        { t: 'Inversión', v: fmt$(n(kpi.inversion)) },
        { t: 'Estimada', v: fmt$(n(kpi.tarifa)) },
        { t: "TRP's / GRPs", v: fmtN(n(kpi.trp)) },
        { t: 'Avisos', v: n(kpi.avisos).toLocaleString() },
        { t: 'Variación', v: kpi.variacionInvPct != null ? `${kpi.variacionInvPct >= 0 ? '+' : ''}${kpi.variacionInvPct.toFixed(1)}%` : '—' },
      ].map((k, i) => (
        <Col key={i} xs={6} md={2}><Card className="border-0 shadow-sm h-100"><Card.Body className="py-2 px-2 text-center">
          <div className="text-muted text-uppercase" style={{ fontSize: 9 }}>{k.t}</div>
          <div className="fw-bold" style={{ fontSize: 15 }}>{k.v}</div>
        </Card.Body></Card></Col>
      ))}
    </Row>
    <Row className="g-3 mb-3">
      <Col lg={12}><ChartCard title="Inversión anual" h={210}>
        {anualChart ? (<>
          <div className="d-flex flex-wrap gap-3 mb-1">
            {anual.map((r, i) => { const prev = i > 0 ? n(anual[i - 1].inversion) : 0; const cur = n(r.inversion); const v = prev > 0 ? (cur - prev) / prev * 100 : null;
              return <div key={r.ano} className="text-center"><div className="fw-bold" style={{ fontSize: 12 }}>{fmt$(cur)}</div><div className="text-muted" style={{ fontSize: 10 }}>{r.ano} {v != null && <span style={{ color: v >= 0 ? '#16a34a' : '#dc2626' }}>{v >= 0 ? '▲' : '▼'}{Math.abs(v).toFixed(1)}%</span>}</div></div>; })}
          </div>
          <div style={{ height: 150 }}><EChart option={anualChart} height={150} onEvents={clickYear} /></div>
        </>) : <NoData />}
      </ChartCard></Col>
    </Row>
    <Row className="g-3 mb-3">
      <Col lg={7}><ChartCard title="Inversión mensual" h={290}>{mensualChart ? <EChart option={mensualChart} height={290} onEvents={clickMonth} /> : <NoData />}</ChartCard></Col>
      <Col lg={5}><ChartCard title="Share por marca" h={290}>{donaMarca ? <EChart option={donaMarca} height={290} onEvents={clickAdd('marcas')} /> : <NoData />}</ChartCard></Col>
    </Row>
    <Row className="g-3 mb-3">
      <Col lg={8}><ChartCard title="Inversión semanal — Marcas" h={290}>{semanalChart ? <EChart option={semanalChart} height={290} /> : <NoData />}</ChartCard></Col>
      <Col lg={4}><ChartCard title="Share por medio" h={290}>{donaMedio ? <EChart option={donaMedio} height={290} onEvents={clickAdd('medios')} /> : <NoData />}</ChartCard></Col>
    </Row>
    {/* ── Enriquecido para gerencia ── */}
    <Row className="g-3 mb-3">
      <Col lg={7}><ChartCard title="Inversión por medio — por año" h={280}>{shareMedioChart ? <EChart option={shareMedioChart} height={280} onEvents={{ click: (p) => { if (p?.seriesName) drill({ grupoMedio: p.seriesName }); } }} /> : <NoData />}</ChartCard></Col>
      <Col lg={5}><ChartCard title="Por género de programa" h={280}>{generoChart ? <EChart option={generoChart} height={280} onEvents={clickAdd('generos')} /> : <NoData />}</ChartCard></Col>
    </Row>
    <Row className="g-3 mb-3">
      <Col lg={6}><ChartCard title="Top categorías" h={300}>{catChart ? <EChart option={catChart} height={300} onEvents={clickAdd('categorias')} /> : <NoData />}</ChartCard></Col>
      <Col lg={6}><ChartCard title="Top sectores" h={300}>{sectorChart ? <EChart option={sectorChart} height={300} onEvents={clickAdd('sectores')} /> : <NoData />}</ChartCard></Col>
    </Row>
    <Row className="g-3 mb-3">
      <Col lg={6}><ChartCard title="Top emisoras / canales" h={300}>{emisoraChart ? <EChart option={emisoraChart} height={300} onEvents={clickAdd('redes')} /> : <NoData />}</ChartCard></Col>
      <Col lg={6}><ChartCard title="Franjas horarias / bloque" h={300}>{franjaChart ? <EChart option={franjaChart} height={300} onEvents={clickAdd('bloques')} /> : <NoData />}</ChartCard></Col>
    </Row>
    <Row className="g-3 mb-3"><Col lg={12}><Card className="border-0 shadow-sm"><Card.Body className="p-2">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="d-flex align-items-center gap-2">
          <h6 className="text-uppercase fw-bold mb-0" style={{ fontSize: 11 }}>🧠 Presión competitiva (IA)</h6>
          {narrativa?.provider && <Badge bg="light" text="dark" style={{ fontSize: 9 }} title={narrativa.model || ''}>{narrativa.provider}</Badge>}
          {narrativa?.cached && <Badge bg="light" text="secondary" style={{ fontSize: 9 }} title={narrativa.generatedAt ? `Generado ${new Date(narrativa.generatedAt).toLocaleString()}` : ''}>💾 guardado</Badge>}
        </div>
        <Button size="sm" variant="outline-primary" onClick={() => onNarrativa(true)} disabled={narrLoading} style={{ fontSize: 11 }}>{narrLoading ? 'Analizando…' : (narrativa ? 'Regenerar' : 'Generar análisis')}</Button>
      </div>
      {narrLoading && !narrativa ? <p className="text-muted small mb-0">Analizando la competencia…</p>
        : narrativa ? (<>
        <p className="small mb-2">{narrativa.narrativa}</p>
        <div className="d-flex flex-wrap gap-2">{(narrativa.clasificacion || []).map((c, i) => <Badge key={i} bg={tipoColor[c.tipo] || 'secondary'} title={c.razon} style={{ fontSize: 11 }}>{c.marca}: {c.tipo}</Badge>)}</div>
      </>) : <p className="text-muted small mb-0">Pulsa "Generar análisis" para clasificar marcas (Líder / Estacional / Seguidor / Táctico) y describir la presión competitiva.</p>}
    </Card.Body></Card></Col></Row>
    <Row className="g-3 mb-3"><Col lg={12}><Card className="border-0 shadow-sm"><Card.Body className="p-2">
      <h6 className="text-uppercase fw-bold" style={{ fontSize: 11 }}>Inversión histórica — Marcas</h6>
      {histRows.length ? <div className="table-responsive small" style={{ maxHeight: 340, overflow: 'auto' }}>
        <table className="table table-sm table-hover mb-0">
          <thead className="table-light sticky-top"><tr><th>Marca</th>{histYears.map(y => <th key={y} className="text-end">{y}</th>)}</tr></thead>
          <tbody>
            {histRows.map(row => <tr key={row.marca}><td className="fw-semibold" style={{ cursor: 'pointer', color: '#2563eb' }} onClick={() => { const cur = applied.marcas || []; if (!cur.includes(row.marca)) drill({ marcas: [...cur, row.marca] }); }} title="Drill: filtrar por esta marca">{row.marca}</td>{row.cells.map((v, i) => { const share = totByYear[histYears[i]] > 0 ? v / totByYear[histYears[i]] * 100 : 0; return <td key={i} className="text-end">{fmt$(v)}<span className="text-muted d-block" style={{ fontSize: 9 }}>{share.toFixed(1)}%</span></td>; })}</tr>)}
            <tr className="table-light fw-bold"><td>Total</td>{histYears.map(y => <td key={y} className="text-end">{fmt$(totByYear[y])}</td>)}</tr>
          </tbody>
        </table>
      </div> : <NoData />}
    </Card.Body></Card></Col></Row>
  </div>);
}

/* ═══════════════ DIGITAL VIEW (propio, FactMetaAd vía budgetService) ═══════════════ */
function DigitalView({ data }) {
  if (!data) return <NoData />;
  const kpi = data.kpis || {};
  const byPlat = (data.byPlatform || []).filter(p => p.platform);
  const byProd = (data.byProductType || []).filter(p => p.productType);
  const daily = data.dailyTrend || data.evolutive || [];
  const campaigns = [...(data.campaigns || [])].sort((a, b) => n(b.cost) - n(a.cost)).slice(0, 20);

  // Inversión diaria del mes (Digital trae un solo mes)
  const dailySorted = [...daily].sort((a, b) => String(a.dateKey).localeCompare(String(b.dateKey)));
  const dailyChart = dailySorted.length ? {
    tooltip: { trigger: 'axis', formatter: p => `${p[0].name}: ${fmt$(p[0].value)}` },
    grid: { left: 58, right: 16, top: 16, bottom: 34 },
    xAxis: { type: 'category', data: dailySorted.map(d => { const k = String(d.dateKey); return k.length >= 8 ? `${k.slice(6, 8)}/${k.slice(4, 6)}` : k; }), axisLabel: { fontSize: 8, interval: 'auto' } },
    yAxis: { type: 'value', axisLabel: { formatter: v => fmt$(v), fontSize: 9 } },
    series: [{ type: 'line', data: dailySorted.map(d => n(d.cost)), smooth: true, areaStyle: { color: 'rgba(24,119,242,0.12)' }, lineStyle: { color: '#1877f2', width: 2 }, itemStyle: { color: '#1877f2' }, showSymbol: false }],
  } : null;

  const platColor = (name) => PLAT_COLOR[name] || null;
  const donaPlat = byPlat.length ? donutOption(byPlat.map(p => ({ name: p.platform, value: n(p.cost) })), platColor) : null;
  const barPlat = byPlat.length ? barOption(byPlat.map(p => p.platform), byPlat.map(p => n(p.cost)), platColor) : null;
  const donaProd = byProd.length ? donutOption(byProd.map(p => ({ name: p.productType, value: n(p.cost) }))) : null;

  return (<>
    <Row className="g-2 mb-2">
      {[
        { t: 'Inversión', v: fmt$(n(kpi.cost)) },
        { t: 'Impresiones', v: fmtN(n(kpi.impressions)) },
        { t: 'Clicks', v: fmtN(n(kpi.clicks)) },
        { t: 'Leads', v: n(kpi.leads).toLocaleString() },
        { t: 'CPL', v: fmt$(n(kpi.cpl)) },
        { t: 'CTR', v: `${n(kpi.ctr).toFixed(2)}%` },
      ].map((k, i) => (
        <Col key={i} xs={6} md={2}><Card className="border-0 shadow-sm h-100"><Card.Body className="py-2 px-2 text-center">
          <div className="text-muted text-uppercase" style={{ fontSize: 9 }}>{k.t}</div>
          <div className="fw-bold" style={{ fontSize: 15 }}>{k.v}</div>
        </Card.Body></Card></Col>
      ))}
    </Row>
    <Row className="g-3 mb-3">
      <Col lg={7}><ChartCard title="Inversión diaria (mes)" h={290}>{dailyChart ? <EChart option={dailyChart} height={290} /> : <NoData />}</ChartCard></Col>
      <Col lg={5}><ChartCard title="Share por plataforma" h={290}>{donaPlat ? <EChart option={donaPlat} height={290} /> : <NoData />}</ChartCard></Col>
    </Row>
    <Row className="g-3 mb-3">
      <Col lg={6}><ChartCard title="Inversión por plataforma" h={280}>{barPlat ? <EChart option={barPlat} height={280} /> : <NoData />}</ChartCard></Col>
      <Col lg={6}><ChartCard title="Share por producto" h={280}>{donaProd ? <EChart option={donaProd} height={280} /> : <NoData />}</ChartCard></Col>
    </Row>
    <Row className="g-3 mb-3"><Col lg={12}><Card className="border-0 shadow-sm"><Card.Body className="p-2">
      <h6 className="text-uppercase fw-bold" style={{ fontSize: 11 }}>Top campañas (inversión)</h6>
      {campaigns.length ? <div className="table-responsive small" style={{ maxHeight: 360, overflow: 'auto' }}>
        <table className="table table-sm table-hover mb-0">
          <thead className="table-light sticky-top"><tr><th>#</th><th>Campaña</th><th>Plataforma</th><th className="text-end">Inversión</th><th className="text-end">Impr.</th><th className="text-end">Clicks</th><th className="text-end">Leads</th><th className="text-end">CPL</th><th className="text-end">CTR</th></tr></thead>
          <tbody>{campaigns.map((c, i) => (
            <tr key={i}><td>{i + 1}</td><td>{c.campaignName}</td><td>{c.sourcePlatform}</td>
              <td className="text-end">{fmt$(n(c.cost))}</td><td className="text-end">{fmtN(n(c.impressions))}</td><td className="text-end">{fmtN(n(c.clicks))}</td>
              <td className="text-end">{n(c.leads).toLocaleString()}</td><td className="text-end">{fmt$(n(c.cpl))}</td><td className="text-end">{n(c.ctr).toFixed(2)}%</td></tr>
          ))}</tbody>
        </table>
      </div> : <NoData />}
    </Card.Body></Card></Col></Row>
  </>);
}

/* ═══════════════ DATA VIEW (tabla plana ATL para descarga) ═══════════════ */
function DataView({ data }) {
  const marcas = data?.porMarca || [];
  return (
    <Card className="border-0 shadow-sm"><Card.Body className="p-2">
      <h6 className="text-uppercase fw-bold" style={{ fontSize: 11 }}>Datos — Ranking de marcas (usa "Descargar datos" para el detalle completo)</h6>
      {marcas.length ? <div className="table-responsive small" style={{ maxHeight: 520, overflow: 'auto' }}>
        <table className="table table-sm table-hover mb-0">
          <thead className="table-light sticky-top"><tr><th>#</th><th>Marca</th><th className="text-end">Inversión</th><th className="text-end">SOI%</th><th className="text-end">SOV%</th><th className="text-end">TRP's</th><th className="text-end">Avisos</th></tr></thead>
          <tbody>{marcas.slice(0, 40).map((r, i) => (
            <tr key={r.marca} style={{ color: r.isFocus ? RD : undefined, fontWeight: r.isFocus ? 700 : 400 }}>
              <td>{i + 1}</td><td>{r.marca}</td><td className="text-end">{fmt$(n(r.inversion))}</td>
              <td className="text-end">{n(r.soiInv).toFixed(2)}%</td><td className="text-end">{n(r.sovPct).toFixed(2)}%</td>
              <td className="text-end">{n(r.trp).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td><td className="text-end">{n(r.avisos).toLocaleString()}</td></tr>
          ))}</tbody>
        </table>
      </div> : <NoData />}
    </Card.Body></Card>
  );
}
